import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Fix: third-party scripts (observed: the Vitals reviews app) can rebuild parts of the
    // product-details DOM a few seconds after page load. When that happens, this particular
    // <product-price> instance is silently orphaned -- detached from the document, but its
    // 'variant:update' listener stays registered on the section (listeners survive on the
    // section element itself), so it keeps "updating" an element nobody can see, while the
    // actually-visible product-price never changes. If we've been orphaned, retarget the
    // update to whichever live, connected <product-price> shares our block id.
    let self = this;
    if (!self.isConnected) {
      const ownBlockId = self.dataset.blockId ?? '';
      const ownSuffix = ownBlockId.includes('__') ? ownBlockId.split('__').pop() : ownBlockId;
      const live = Array.from(document.querySelectorAll('product-price')).find((el) => {
        if (!el.isConnected) return false;
        const candidateId = el instanceof HTMLElement ? el.dataset.blockId ?? '' : '';
        const candidateSuffix = candidateId.includes('__') ? candidateId.split('__').pop() : candidateId;
        return candidateSuffix === ownSuffix;
      });
      if (live instanceof ProductPrice) self = live;
    }

    const { priceContainer, volumePricingNote } = self.refs;
    // Find the new product-price element in the updated HTML.
    // Prefer an exact data-block-id match (the common case for a raw full-page fetch), but fall
    // back to matching on the block id's stable suffix (after "__"): responses coming back from
    // the Section Rendering API (?section_id=...) reuse a fresh random per-render hash prefix on
    // block ids, so an exact match never succeeds there even though the block is the same one.
    const blockId = self.dataset.blockId ?? '';
    const blockIdSuffix = blockId.includes('__') ? blockId.split('__').pop() : blockId;
    const newProductPrice =
      event.detail.data.html.querySelector(`product-price[data-block-id="${blockId}"]`) ??
      Array.from(event.detail.data.html.querySelectorAll('product-price')).find((el) => {
        const candidateId = el instanceof HTMLElement ? el.dataset.blockId ?? '' : '';
        const candidateSuffix = candidateId.includes('__') ? candidateId.split('__').pop() : candidateId;
        return candidateSuffix === blockIdSuffix;
      });
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${self.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (self.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
